import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, GraduationCap } from "lucide-react";
import { MaskLine, Reveal } from "./primitives";
import { NODE_FLOW, IMAGES } from "@/data/site";
import { useModal } from "./Forms";

const FLOAT_STATS = [
  { label: "Since 2015", pos: "left-[4%] top-[26%]" },
  { label: "100+ Professionals", pos: "right-[5%] top-[20%]" },
  { label: "50+ Institutions", pos: "right-[8%] bottom-[26%]" },
  { label: "Pan-India Network", pos: "left-[6%] bottom-[22%]" },
];

// Growth engine node ring visual
function GrowthEngine() {
  const R = 168;
  const cx = 220, cy = 220;
  const nodes = NODE_FLOW.map((label, i) => {
    const angle = (i / NODE_FLOW.length) * Math.PI * 2 - Math.PI / 2;
    return { label, x: cx + R * Math.cos(angle), y: cy + R * Math.sin(angle), i };
  });

  return (
    <svg viewBox="0 0 440 440" className="h-full w-full">
      <defs>
        <radialGradient id="coreGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#C6FF3A" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#22C55E" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="edge" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#C6FF3A" />
          <stop offset="100%" stopColor="#22C55E" />
        </linearGradient>
      </defs>

      {/* rotating ring */}
      <motion.g
        style={{ transformOrigin: "220px 220px" }}
        animate={{ rotate: 360 }}
        transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
      >
        <circle cx={cx} cy={cy} r={R} fill="none" stroke="rgba(198,255,58,0.18)" strokeWidth="1" strokeDasharray="4 8" />
      </motion.g>

      {/* edges to center */}
      {nodes.map((n) => (
        <motion.line
          key={"l" + n.i}
          x1={cx} y1={cy} x2={n.x} y2={n.y}
          stroke="url(#edge)" strokeWidth="1" strokeOpacity="0.35"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: 0.4 }}
          transition={{ duration: 1, delay: 0.6 + n.i * 0.08 }}
        />
      ))}

      {/* connecting arc between sequential nodes */}
      {nodes.map((n, i) => {
        const next = nodes[(i + 1) % nodes.length];
        return (
          <motion.line
            key={"e" + i}
            x1={n.x} y1={n.y} x2={next.x} y2={next.y}
            stroke="rgba(198,255,58,0.25)" strokeWidth="1"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 1.2, delay: 1 + i * 0.1 }}
          />
        );
      })}

      {/* core */}
      <circle cx={cx} cy={cy} r="70" fill="url(#coreGlow)" />
      <circle cx={cx} cy={cy} r="34" fill="#0B130C" stroke="#C6FF3A" strokeWidth="1.5" />
      <text x={cx} y={cy - 2} textAnchor="middle" className="fill-white font-display" fontSize="16" fontWeight="700">A3</text>
      <text x={cx} y={cy + 12} textAnchor="middle" className="fill-brand-cyan" fontSize="6" letterSpacing="1.5">GROWTH ENGINE</text>

      {/* nodes */}
      {nodes.map((n) => (
        <motion.g
          key={"n" + n.i}
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.7 + n.i * 0.08 }}
        >
          <circle cx={n.x} cy={n.y} r="5.5" fill="#C6FF3A">
            <animate attributeName="r" values="5.5;7;5.5" dur="2.5s" begin={`${n.i * 0.3}s`} repeatCount="indefinite" />
          </circle>
          <circle cx={n.x} cy={n.y} r="11" fill="none" stroke="#C6FF3A" strokeOpacity="0.3" />
          <text
            x={n.x} y={n.y - 16}
            textAnchor="middle"
            className="fill-white/70 font-mono"
            fontSize="7.5"
          >
            {n.label}
          </text>
        </motion.g>
      ))}
    </svg>
  );
}

export function Hero() {
  const { openInstitution, openStudent } = useModal();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yVisual = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section id="home" ref={ref} className="relative min-h-screen overflow-hidden bg-ink-0 pt-28" data-testid="hero">
      <div
        className="absolute inset-0 opacity-50"
        style={{ backgroundImage: `url(${IMAGES.heroBg})`, backgroundSize: "cover", backgroundPosition: "center" }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-ink-0/70 via-ink-0/60 to-ink-0" />
      <div className="absolute inset-0 bg-grid opacity-40" />
      <div className="absolute inset-0 bg-radial-glow" />
      <div className="pointer-events-none absolute -top-40 left-1/2 h-[600px] w-[600px] -translate-x-1/2 rounded-full bg-brand-blue/20 blur-[160px]" />

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-10 px-5 pb-20 lg:grid-cols-2 lg:px-8">
        {/* Left copy */}
        <div className="relative z-10">
          <Reveal>
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 font-mono text-[11px] uppercase tracking-widest text-brand-cyan">
              <span className="h-1.5 w-1.5 rounded-full bg-brand-cyan" /> Education Growth Partner · Since 2015
            </div>
          </Reveal>

          <h1 className="mt-6 font-display text-4xl font-bold leading-[0.98] tracking-tight text-white sm:text-5xl lg:text-6xl xl:text-[4.4rem]">
            <MaskLine delay={0.1}>YOU OWN</MaskLine>
            <MaskLine delay={0.22}>THE COLLEGE.</MaskLine>
            <MaskLine delay={0.34} className="text-gradient">WE BUILD THE</MaskLine>
            <MaskLine delay={0.46} className="text-gradient">GROWTH ENGINE.</MaskLine>
          </h1>

          <Reveal delay={0.7}>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-white/70 md:text-lg">
              End-to-end technology, marketing, admissions, student acquisition and education solutions for the next generation of MBA &amp; PGDM institutions.
            </p>
          </Reveal>

          <Reveal delay={0.8}>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-white/60">
              Since 2015, A3 Global Marketing &amp; Education has helped institutions strengthen their digital presence, generate student demand, manage admissions and build sustainable growth.
            </p>
          </Reveal>

          <Reveal delay={0.9}>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <button onClick={openInstitution} className="group inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-brand-blue to-brand-cyan px-7 py-3.5 font-medium text-[#08120A] transition-shadow hover:shadow-[0_0_30px_rgba(198,255,58,0.45)]" data-testid="hero-partner">
                Partner With A3 <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </button>
              <button onClick={openStudent} className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 bg-white/5 px-7 py-3.5 font-medium text-white backdrop-blur-md transition-colors hover:border-brand-cyan/50 hover:bg-white/10" data-testid="hero-find-college">
                <GraduationCap className="h-4 w-4 text-brand-cyan" /> Find Your Dream College
              </button>
            </div>
          </Reveal>
        </div>

        {/* Right visual */}
        <motion.div style={{ y: yVisual, opacity }} className="relative mx-auto aspect-square w-full max-w-[520px]">
          {FLOAT_STATS.map((s, i) => (
            <motion.div
              key={s.label}
              className={`absolute ${s.pos} z-20 rounded-xl border border-white/10 bg-ink-1/70 px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-white/80 backdrop-blur-md`}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1, y: [0, -8, 0] }}
              transition={{ opacity: { delay: 1.2 + i * 0.15 }, scale: { delay: 1.2 + i * 0.15 }, y: { duration: 4 + i, repeat: Infinity, ease: "easeInOut" } }}
            >
              <span className="text-brand-cyan">◆</span> {s.label}
            </motion.div>
          ))}
          <GrowthEngine />
        </motion.div>
      </div>

      {/* scroll cue */}
      <div className="absolute bottom-6 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-2 lg:flex">
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">Scroll</span>
        <div className="h-10 w-px bg-gradient-to-b from-brand-cyan to-transparent" />
      </div>
    </section>
  );
}
